import {
  clampThresholdSize,
  getSizesFromBands,
  ShootTimeBand,
} from "portal/utils/shootTime";
import { useCallback, useEffect, useRef, useState } from "react";

/** Local drag preview so pointer moves don't remount/re-render the parent tree. */
export const useThresholdDragState = (
  bands: ShootTimeBand[],
  maxRadiusMm: number,
  onSizesChange: ((sizes: number[]) => void) | undefined
): {
  activeThresholdIndex: number | undefined;
  displaySizes: number[];
  onActivateThreshold: (index: number) => void;
  onDragStart: (index: number) => void;
  onDragMove: (index: number, radiusMm: number) => void;
  onDragEnd: (index: number) => void;
} => {
  const committedSizes = getSizesFromBands(bands);

  const committedKey = committedSizes.join(",");
  const [prevCommittedKey, setPrevCommittedKey] = useState(committedKey);

  const [draftSizes, setDraftSizes] = useState<number[] | undefined>();
  const draftRef = useRef<number[] | undefined>(); // ref version of draft sizes so that we can update without waiting for render (smoother drag)
  const displaySizes = draftSizes ?? committedSizes;

  const [activeThresholdIndex, setActiveThresholdIndex] = useState<
    number | undefined
  >();

  const draggingIndexRef = useRef<number | undefined>();
  const rafRef = useRef<number | undefined>();

  // Drop local draft when parent sizes change (and we're not mid-drag).
  if (
    committedKey !== prevCommittedKey &&
    draggingIndexRef.current === undefined
  ) {
    setPrevCommittedKey(committedKey);
    setDraftSizes(undefined);
    draftRef.current = undefined;
  } else if (committedKey !== prevCommittedKey) {
    setPrevCommittedKey(committedKey);
  }

  useEffect(() => {
    return () => {
      if (rafRef.current !== undefined) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const onActivateThreshold = useCallback((index: number) => {
    setActiveThresholdIndex(index);
  }, []);

  const onDragStart = useCallback(
    (index: number) => {
      draggingIndexRef.current = index;
      setActiveThresholdIndex(index);
      const next = [...committedSizes];
      draftRef.current = next;
      setDraftSizes(next);
    },
    [committedSizes]
  );

  const onDragMove = useCallback(
    (index: number, radiusMm: number) => {
      const base = draftRef.current ?? committedSizes;
      const next = clampThresholdSize(base, index, radiusMm, maxRadiusMm);
      draftRef.current = next;
      if (rafRef.current !== undefined) {
        return;
      }
      // only make updates based on how fast we can reasonably render frames so that we don't fire more changes than can be seen (prevents drag jitters)
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = undefined;
        if (draftRef.current) {
          setDraftSizes(draftRef.current);
        }
      });
    },
    [committedSizes, maxRadiusMm]
  );

  const onDragEnd = useCallback(
    (_index: number) => {
      if (rafRef.current !== undefined) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = undefined;
      }
      draggingIndexRef.current = undefined;
      const finalSizes = draftRef.current;
      if (finalSizes) {
        setDraftSizes(finalSizes);
        onSizesChange?.(finalSizes);
      }
    },
    [onSizesChange]
  );

  return {
    displaySizes,
    activeThresholdIndex,
    onActivateThreshold,
    onDragStart,
    onDragMove,
    onDragEnd,
  };
};
